import isAscii from 'validator/lib/isAscii';
import isCurrency from 'validator/lib/isCurrency';
import isEmail from 'validator/lib/isEmail';
import isLength from 'validator/lib/isLength';

import { REFERRAL_USER_ROLE } from '~/shared/constants/referral-user-roles';
import { isEmpty } from '~/shared/helpers/check-helpers';
import { API_HOST, LIMIT_OF_CHARACTERS, USER_ROLE } from '~/universal/constants';
import { removeSrcAttributeValuesFromHtml } from '~/universal/helpers/validation-helpers';

export const getValidatePhone = (initialUser, validation) => () => {
  const validationRules = [
    validation.phoneValid,
    validation.isInRange.bind(null, {
      min: LIMIT_OF_CHARACTERS.PHONE_NUMBER.MIN,
      max: LIMIT_OF_CHARACTERS.PHONE_NUMBER.MAX,
    }),
  ];

  if (
    initialUser?.role === USER_ROLE.TEACHER ||
    initialUser?.role === USER_ROLE.SUPER_TEACHER ||
    initialUser?.role === USER_ROLE.MANAGER ||
    initialUser?.role === USER_ROLE.AUTHOR ||
    initialUser?.role === REFERRAL_USER_ROLE.PARTNER ||
    initialUser?.role === REFERRAL_USER_ROLE.MANAGER ||
    initialUser?.role === REFERRAL_USER_ROLE.FOUNDER ||
    initialUser?.role === USER_ROLE.ADMIN
  ) {
    validationRules.unshift(validation.required);
  }

  return validate(validationRules);
};

export const validator = ({ t, lng }: { t: (key: string, options: { ns: string[] }) => string }) => ({
  required: (value: unknown) => {
    if (typeof value === 'string') {
      return value.trim() ? null : t('text.required-field');
    }

    if (isEmpty(value) || value === false || (value?.valid && !value?.valid?.())) {
      return t('text.required-field');
    }

    return null;
  },
  validateAtLeastOneItemInArray: (value) => {
    if (!Array.isArray(value) || value.length === 0) {
      return t('text.required-field');
    }

    return null;
  },
  isNotRestrictedLink: async (value) => {
    if (!value || typeof value !== 'string') {
      return null;
    }

    try {
      const url = new URL(value);

      if (!['http:', 'https:'].includes(url.protocol)) {
        return t('text.invalid-link-format');
      }

      const locale = ['ua', 'ru', 'en'].includes(String(lng)) ? String(lng) : 'ua';

      const response = await fetch(`${API_HOST}/api/validate-link?url=${encodeURIComponent(value)}`, {
        method: 'GET',
        headers: {
          'X-Locale': locale,
        },
      });

      if (!response.ok) {
        return t('text.link-is-not-available');
      }

      const result = await response.json();

      if (!result?.valid) {
        return result?.message || t('text.link-cannot-be-embedded');
      }

      return null;
    } catch {
      return t('text.link-is-not-available');
    }
  },
  phoneValid: (value) => {
    const toRaw = (v: any) => {
      if (!v) return '';
      if (typeof v === 'string') return v;
      // объект вида { countryCode, areaCode, phoneNumber }
      return `${v.countryCode || ''}${v.areaCode || ''}${v.phoneNumber || ''}`;
    };

    const digits = toRaw(value).replace(/\D/g, '');

    if (!digits) {
      return null;
    }

    // Если у вас основная страна — Украина:
    if (digits.startsWith('380')) {
      return digits.length === 12 ? null : t('text.required-field');
    }

    // Универсальная проверка: 10–15 цифр для E.164
    return digits.length >= 10 && digits.length <= 15 ? null : t('text.required-field');
  },
  isEmail: (value) => (!value || !isEmail(value) ? t('error.validation.email') : null),
  isAlphanumeric: (options, value) => {
    if (value && !isAscii(value, options) && !Number.isInteger(Number(value))) {
      return t('error.validation.alpha-numeric');
    }

    return null;
  },
  isAlpha: (options, value) => {
    if (value && !isAscii(value, options)) {
      return t('error.validation.alpha');
    }

    return null;
  },
  isInRange: (options, data) => {
    let value = typeof data === 'object' ? data?.value : data;

    if (options?.ignoreHtmlSrcAttributes && typeof value === 'string') {
      value = removeSrcAttributeValuesFromHtml(value);
    }

    if (value && !isLength(value, options)) {
      return `${t('error.validation.length', {
        from: options.min,
        to: options.max ? options.max : '∞',
      })}`;
    }

    return null;
  },
  isAscii: (value) => {
    if (value && !isAscii(value)) {
      return t('error.validation.alpha-and-dashes');
    }

    return null;
  },
  isCurrency: (options, value) => {
    if (value && !isCurrency(value?.toString(), options)) {
      return t('error.validation.currency');
    }

    return null;
  },
  boolean: (value) => {
    if (value === false) {
      return t('text.required-field');
    }

    return null;
  },
  emptyArray: (value) => {
    if (isEmpty(value)) {
      return t('text.required-field');
    }

    return null;
  },
  alphabet: (value) => {
    if (!/^[a-zA-Z\s]+$/.test(value)) {
      return t('text.alphabet-only');
    }
    return null;
  },
  isInteger: (value) => {
    if (!Number.isInteger(Number(value))) {
      return t('text.integer-only');
    }
    return null;
  },
});

export const validate = (validations) => (value) => {
  let error = null;

  validations.forEach((cb) => {
    const result = cb(value);

    if (result) {
      error = result;
    }
  });

  return error;
};
